import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
const toast = { success: (m) => console.log("[toast]", m), error: (m) => console.error("[toast]", m), info: (m) => console.log("[toast]", m) };
import PageContainer, { PageHeader } from "@/components/PageContainer";
import api from "@/lib/assessments";
import { readAll } from "@/lib/assessments";
const formatApiError = (e) => (e && e.message) || "Unknown error";
import { Save, ChevronLeft } from "lucide-react";
import { ASAM_DIMENSIONS_TOOL, ASAM_RISK_SCALE, interpretASAM } from "@/data/instruments";

export default function ASAMScorer() {
  const navigate = useNavigate();
  const [patient, setPatient] = useState("");
  const [notes, setNotes] = useState("");
  const [scores, setScores] = useState({});
  const [dimNotes, setDimNotes] = useState({});
  const [saving, setSaving] = useState(false);

  const complete = ASAM_DIMENSIONS_TOOL.every((d) => scores[d.n] !== undefined);
  const interp = interpretASAM(scores);

  const save = async () => {
    if (!complete) {
      toast.error("Please score every dimension.");
      return;
    }
    setSaving(true);
    try {
      const responses = {};
      ASAM_DIMENSIONS_TOOL.forEach((d) => {
        responses[`dim${d.n}`] = { score: scores[d.n], note: dimNotes[d.n] || "" };
      });
      await api.post("/assessments", {
        kind: "asam",
        patient_identifier: patient || null,
        responses,
        score: Math.max(0, ...Object.values(scores)),
        interpretation: interp.band,
        notes: notes || null,
      });
      toast.success("ASAM scoring saved to history.");
      navigate("/portal/continuum/tools/history");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer testid="asam-page">
      <Link
        to="/portal/continuum/tools"
        className="text-sm text-ink-muted hover:text-navy inline-flex items-center gap-1 mb-4"
        data-testid="asam-back"
      >
        <ChevronLeft size={14} strokeWidth={2} /> All tools
      </Link>

      <PageHeader
        overline="Portal · Tool"
        title="ASAM Dimension Scorer"
        subtitle="Score the six ASAM 4th Edition dimensions (0–4). The recommended level-of-care band is the maximum severity across all six."
      />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="card p-5">
            <label className="label">Patient identifier</label>
            <input
              className="field"
              value={patient}
              onChange={(e) => setPatient(e.target.value)}
              placeholder="Initials or MRN"
              data-testid="asam-patient"
            />
          </div>

          {ASAM_DIMENSIONS_TOOL.map((d) => (
            <div key={d.n} className="card p-5" data-testid={`asam-dim-${d.n}`}>
              <div className="flex items-start gap-4 mb-3">
                <div className="w-9 h-9 rounded-full bg-teal text-cream font-semibold flex items-center justify-center flex-shrink-0">
                  {d.n}
                </div>
                <div>
                  <h3 className="text-navy font-semibold">{d.title}</h3>
                  <p className="text-sm text-ink-muted mt-0.5">{d.prompt}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {ASAM_RISK_SCALE.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setScores({ ...scores, [d.n]: opt.value })}
                    className={`btn text-sm ${scores[d.n] === opt.value ? "btn-primary" : "btn-secondary"}`}
                    data-testid={`asam-dim-${d.n}-score-${opt.value}`}
                    title={opt.detail}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <input
                className="field text-sm"
                value={dimNotes[d.n] || ""}
                onChange={(e) => setDimNotes({ ...dimNotes, [d.n]: e.target.value })}
                placeholder="Dimension-specific note (optional)"
                data-testid={`asam-dim-${d.n}-note`}
              />
            </div>
          ))}

          <div className="card p-5">
            <label className="label">Clinician notes</label>
            <textarea
              className="field min-h-[80px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="asam-notes"
              placeholder="Overall clinical impression."
            />
          </div>
        </div>

        <aside className="lg:sticky lg:top-6 self-start">
          <div className="card p-6" data-testid="asam-summary">
            <div className="overline mb-2">Recommended level of care</div>
            {complete ? (
              <>
                <div className="text-lg font-semibold text-navy leading-snug fade-up">{interp.band}</div>
                <p className="text-sm text-ink-muted mt-2 leading-relaxed">{interp.detail}</p>
              </>
            ) : (
              <div className="text-sm text-ink-muted">
                Score all six dimensions to see the recommendation.
              </div>
            )}

            <div className="rule my-4" />
            <div className="text-xs uppercase tracking-wider text-ink-muted mb-2">Dimension scores</div>
            <ul className="text-sm space-y-1">
              {ASAM_DIMENSIONS_TOOL.map((d) => (
                <li key={d.n} className="flex items-center justify-between text-ink">
                  <span className="truncate mr-2">Dim {d.n}</span>
                  <span className="font-medium text-navy">
                    {scores[d.n] !== undefined ? scores[d.n] : "—"}
                  </span>
                </li>
              ))}
            </ul>

            <div className="rule my-4" />

            <button
              onClick={save}
              disabled={!complete || saving}
              className="btn btn-primary w-full justify-center"
              data-testid="asam-save"
            >
              {saving ? "Saving..." : (
                <>
                  <Save size={14} strokeWidth={2} /> Save to history
                </>
              )}
            </button>
          </div>
        </aside>
      </div>
    </PageContainer>
  );
}
